/* eslint-disable */
// src/forms/local/deleteLocalForm.js

import React, { Component } from "react";
import PropTypes from "prop-types";
import { Grid, Container, Paper, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";

import localService from "../../services/localService";
import localMachineService from "../../services/localMachineService";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";

class DeleteLocalForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      local: props.local || {},
      submitting: false,
      effectiveDate: getDefaultOperationDate(),
      hasActiveMachines: false,
      localCreatedAtDate: parseDateOnlyToLocalDate(dateOnlyFromDateTime(props.local?.CreatedAt)),
      latestFinishDate: null,
      generalError: "",
    };
  }

  async componentDidMount() {
    const localId = this.state.local?.Id;
    if (!localId) return;

    try {
      const [activeLocalMachines, inactiveLocalMachines] = await Promise.all([
        localMachineService.getActiveLocalMachineByLocal(localId),
        localMachineService.getInactiveLocalMachineByLocal(localId),
      ]);

      const latestFinishDate = (inactiveLocalMachines || [])
        .map((localMachine) =>
          parseDateOnlyToLocalDate(dateOnlyFromDateTime(localMachine?.FinishDateTime))
        )
        .filter(Boolean)
        .reduce((latest, current) => {
          if (!latest) return current;
          return current.getTime() >= latest.getTime() ? current : latest;
        }, null);

      this.setState({
        hasActiveMachines: Array.isArray(activeLocalMachines) && activeLocalMachines.length > 0,
        latestFinishDate,
      });
    } catch (error) {
      console.error("Error al cargar el estado temporal del local:", error);
    }
  }

  handleDateChange = (date) => {
    this.setState({ effectiveDate: date, generalError: "" });
  };

  getMinDate = () => {
    const { localCreatedAtDate, latestFinishDate } = this.state;

    if (!localCreatedAtDate) return latestFinishDate;
    if (!latestFinishDate) return localCreatedAtDate;

    return latestFinishDate.getTime() >= localCreatedAtDate.getTime()
      ? latestFinishDate
      : localCreatedAtDate;
  };

  getEffectiveDateErrorMessage = () => {
    const { effectiveDate, localCreatedAtDate, latestFinishDate } = this.state;

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }

    const minDate = this.getMinDate();
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      const latestFinishDominates =
        latestFinishDate &&
        (!localCreatedAtDate || latestFinishDate.getTime() >= localCreatedAtDate.getTime());

      if (latestFinishDominates) {
        return "La fecha de cierre no puede ser anterior al último egreso de una máquina de este depósito.";
      }

      return "La fecha de cierre no puede ser anterior a la fecha de creación del depósito.";
    }

    return "";
  };

  handleDelete = async () => {
    const { local, effectiveDate, hasActiveMachines } = this.state;

    if (hasActiveMachines) {
      this.setState({
        generalError: "Retirá las máquinas antes de cerrar el depósito.",
      });
      return;
    }

    const effectiveDateError = this.getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }

    this.setState({ submitting: true, generalError: "" });

    try {
      const result = await localService.deleteLocal(local.Id, {
        EffectiveDate: toLocalDateOnlyString(effectiveDate),
      });

      if (result?.conflict) {
        this.setState({ generalError: "Retirá las máquinas antes de cerrar el depósito." });
        return;
      }

      if (result?.invalid) {
        this.setState({ generalError: result.message || "No se pudo cerrar el depósito." });
        return;
      }

      if (result?.ok) {
        if (typeof this.props.onDelete === "function") this.props.onDelete();
        else if (typeof this.props.onDeleteSuccess === "function") this.props.onDeleteSuccess();

        this.props.closeForm();
      } else {
        this.setState({ generalError: "No se pudo cerrar el depósito." });
      }
    } catch (error) {
      console.error("Hubo un error al cerrar el depósito:", error);
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const {
      submitting,
      effectiveDate,
      hasActiveMachines,
      generalError,
    } = this.state;
    const effectiveDateError = this.getEffectiveDateErrorMessage();
    const today = getDefaultOperationDate();
    const minDate = this.getMinDate();

    return (
      <Container component={Paper} style={{ padding: "20px", marginTop: "20px" }}>
        <Grid container spacing={2}>
          {hasActiveMachines && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                Retirá las máquinas antes de cerrar el depósito.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
              <DatePicker
                label="Fecha de cierre"
                value={effectiveDate}
                onChange={this.handleDateChange}
                minDate={minDate || undefined}
                maxDate={today}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(effectiveDateError),
                    helperText: effectiveDateError,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {generalError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                {generalError}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CancelButton onClick={this.props.closeForm} disabled={submitting} fullWidth>
                  Cancelar
                </CancelButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <AcceptButton
                  onClick={this.handleDelete}
                  disabled={submitting || hasActiveMachines || Boolean(effectiveDateError)}
                  fullWidth
                >
                  {submitting ? "Cerrando depósito..." : "Cerrar depósito"}
                </AcceptButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    );
  }
}

DeleteLocalForm.propTypes = {
  closeForm: PropTypes.func.isRequired,
  local: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
};

export default DeleteLocalForm;
